/**
 * Toolbar: tool selection and brush/pen size. Tools are grouped by layer; only the selected layer's tools (+ other) are shown.
 */

import type { ToolId, TeePositionColorKey, PinDay } from './types';
import { TEE_POSITION_COLORS, PIN_DAY_COLORS } from './colors';
import {
  SIDEBAR_LAYER_ORDER,
  getToolsForLayer,
  type LayerId,
} from './layers';

const TOOL_LABELS: Record<ToolId, string> = {
  fairway: 'Fairway',
  firstCut: 'Light Rough',
  secondCut: 'Heavy Rough',
  green: 'Green',
  bunker: 'Bunker',
  waste: 'Waste',
  water: 'Water',
  cartPath: 'Cart path',
  outOfBounds: 'Out of bounds',
  hazard: 'Hazard',
  house: 'House',
  tee: 'Tee box',
  tree: 'Tree',
  pin: 'Flag',
  teePosition: 'Tee position',
  marker150: '150 yd',
  elevation: 'Elevation',
  eraser: 'Cut',
  pointer: 'Pointer',
  move: 'Move',
  reshape: 'Reshape',
  duplicate: 'Duplicate',
  measure: 'Measure',
};

/** Keyboard shortcut letter per tool (for underlining in labels). */
const TOOL_SHORTCUT_LETTER: Record<ToolId, string> = {
  fairway: 'f',
  firstCut: 'r',
  secondCut: 'r',
  green: 'g',
  bunker: 'b',
  waste: 'w',
  water: 'w',
  cartPath: 'c',
  outOfBounds: 'o',
  hazard: 'h',
  house: 'h',
  tee: 't',
  tree: 't',
  pin: 'f',
  teePosition: 't',
  marker150: '1',
  elevation: 'e',
  eraser: 'x',
  pointer: 'p',
  move: 'm',
  reshape: 's',
  duplicate: 'd',
  measure: 'l',
};

function labelWithShortcut(label: string, shortcut: string): string {
  const idx = label.toLowerCase().indexOf(shortcut.toLowerCase());
  if (idx < 0) return label;
  const before = label.slice(0, idx);
  const char = label[idx];
  const after = label.slice(idx + 1);
  return `${before}<span class="tool-shortcut">${char}</span>${after}`;
}

const INCH_TO_MM = 25.4;
const FEET_TO_MM = 12 * INCH_TO_MM; // 304.8
const YARD_TO_MM = 36 * INCH_TO_MM; // 914.4

export type BrushUnit = 'inches' | 'feet' | 'yards';

export type TreeSize = 'S' | 'M' | 'L';
export type HouseSize = 'S' | 'M' | 'L';

export type ElevationDirection = 'up' | 'down';
export type ElevationAmountInches = 3 | 6 | 12 | 24;
export type ElevationSize = 'small' | 'medium' | 'large';
export type ElevationEdgeMode = 'smooth' | 'steep';

export interface ToolbarState {
  activeTool: ToolId;
  brushUnit: BrushUnit;
  brushSizeValue: number;
  elevationAmountInches: ElevationAmountInches;
  elevationSize: ElevationSize;
  elevationEdgeMode: ElevationEdgeMode;
  elevationDirection: ElevationDirection;
  treeSize: TreeSize;
  houseSize: HouseSize;
  teePositionColor: TeePositionColorKey;
  pinDay: PinDay;
}

const MIN_INCHES = 1;
const MAX_INCHES = 36;
const MIN_FEET = 0.5;
const MAX_FEET = 3;
const MIN_YARDS = 1;
const MAX_YARDS = 10;

const LAYER_LABELS: Record<LayerId, string> = {
  green: 'Green',
  playingArea: 'Playing area',
  tee: 'Tee',
  hazards: 'Hazards',
};

/** Tools disabled when current hole is par 3 (no fairway or 150 yd marker). */
const PAR3_DISABLED_TOOLS: ToolId[] = ['fairway', 'marker150'];

/** Tool disabled when current hole already has a green (only 1 green per hole). */
const GREEN_DISABLED_WHEN_EXISTS = 'green' as const;

export function createToolbar(
  container: HTMLElement,
  options?: {
    initialLayer?: LayerId;
    onActiveToolChange?: () => void;
    layerVisibility?: Record<LayerId, boolean>;
    onLayerVisibilityChange?: (layerId: LayerId, visible: boolean) => void;
    /** When provided, modifier controls (brush size, tree size, etc.) are rendered here instead of in the toolbar. */
    modifiersContainer?: HTMLElement;
    /** When true, Fairway and 150 yd tools are disabled (par 3 hole). */
    isPar3Hole?: () => boolean;
    /** When true, Green tool is disabled (hole already has a green). */
    hasGreenForCurrentHole?: () => boolean;
  }
) {
  const initialLayer: LayerId = options?.initialLayer ?? 'green';
  const onActiveToolChange = options?.onActiveToolChange;
  const layerVisibility = options?.layerVisibility ?? ({} as Record<LayerId, boolean>);
  const onLayerVisibilityChange = options?.onLayerVisibilityChange;
  const modifiersContainer = options?.modifiersContainer;
  const isPar3Hole = options?.isPar3Hole ?? (() => false);
  const hasGreenForCurrentHole = options?.hasGreenForCurrentHole ?? (() => false);
  const state: ToolbarState = {
    activeTool: 'green',
    brushUnit: 'yards',
    brushSizeValue: 3,
    elevationAmountInches: 6,
    elevationSize: 'medium',
    elevationEdgeMode: 'smooth',
    elevationDirection: 'up',
    treeSize: 'M',
    houseSize: 'M',
    teePositionColor: 'white',
    pinDay: 'thu',
  };

  /** Remember which tool was selected per layer when switching away and back. */
  let currentLayerId: LayerId = initialLayer;
  const activeToolByLayer: Partial<Record<LayerId, ToolId>> = {};

  const root = document.createElement('div');
  root.className = 'toolbar';

  const toolList = document.createElement('div');
  toolList.className = 'toolbar-tools';
  toolList.setAttribute('role', 'toolbar');
  toolList.setAttribute('aria-label', 'Drawing tools');

  // Build layer groups: each parent (Green, Playing Area, Tee, Hazards) with its child tools
  for (const layerId of SIDEBAR_LAYER_ORDER) {
    const group = document.createElement('div');
    group.className = 'toolbar-layer-group';
    group.setAttribute('data-layer', layerId);

    const header = document.createElement('div');
    header.className = 'toolbar-layer-header';
    const check = document.createElement('input');
    check.type = 'checkbox';
    check.checked = layerVisibility[layerId] !== false;
    check.setAttribute('data-layer', layerId);
    check.setAttribute('aria-label', `Show ${LAYER_LABELS[layerId]}`);
    check.addEventListener('change', () => {
      onLayerVisibilityChange?.(layerId, check.checked);
    });
    const label = document.createElement('span');
    label.className = 'toolbar-layer-label';
    label.textContent = LAYER_LABELS[layerId];
    header.appendChild(check);
    header.appendChild(label);
    group.appendChild(header);

    const toolsWrap = document.createElement('div');
    toolsWrap.className = 'toolbar-layer-tools';
    const tools = getToolsForLayer(layerId);
    for (const id of tools) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tool-btn';
      const shortcut = TOOL_SHORTCUT_LETTER[id] ?? '';
      btn.innerHTML = shortcut ? labelWithShortcut(TOOL_LABELS[id], shortcut) : TOOL_LABELS[id];
      btn.setAttribute('data-tool', id);
      btn.setAttribute('data-layer', layerId);
      btn.addEventListener('click', () => {
        if (PAR3_DISABLED_TOOLS.includes(id) && isPar3Hole()) return;
        if (id === GREEN_DISABLED_WHEN_EXISTS && hasGreenForCurrentHole()) return;
        state.activeTool = id;
        activeToolByLayer[currentLayerId] = id;
        currentLayerId = layerId;
        toolList.querySelectorAll('.tool-btn').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        updateSizeVisibility();
        onActiveToolChange?.();
      });
      toolsWrap.appendChild(btn);
    }
    group.appendChild(toolsWrap);
    toolList.appendChild(group);
  }

  function updatePar3DisabledState() {
    const disabled = isPar3Hole();
    for (const id of PAR3_DISABLED_TOOLS) {
      const btn = toolList.querySelector<HTMLButtonElement>(`[data-tool="${id}"]`);
      if (btn) {
        btn.disabled = disabled;
        btn.title = disabled ? 'Not available for par 3 holes' : '';
      }
    }
  }

  function updateGreenDisabledState() {
    const disabled = hasGreenForCurrentHole();
    const btn = toolList.querySelector<HTMLButtonElement>(`[data-tool="${GREEN_DISABLED_WHEN_EXISTS}"]`);
    if (btn) {
      btn.disabled = disabled;
      btn.title = disabled ? 'Only one green per hole' : '';
    }
  }

  function showLayer(layerId: LayerId) {
    activeToolByLayer[currentLayerId] = state.activeTool;
    currentLayerId = layerId;

    const visibleTools = [...getToolsForLayer(layerId)].filter(
      (t) =>
        !(PAR3_DISABLED_TOOLS.includes(t) && isPar3Hole()) &&
        !(t === GREEN_DISABLED_WHEN_EXISTS && hasGreenForCurrentHole())
    );
    const savedTool = activeToolByLayer[layerId];
    const restoreTool =
      (savedTool && visibleTools.includes(savedTool) ? savedTool : visibleTools[0]) ??
      state.activeTool;
    state.activeTool = restoreTool;

    toolList.querySelectorAll('.tool-btn').forEach((b) => b.classList.remove('active'));
    const activeBtn = toolList.querySelector<HTMLButtonElement>(`[data-tool="${restoreTool}"]`);
    if (activeBtn) activeBtn.classList.add('active');
    updatePar3DisabledState();
    updateGreenDisabledState();
    updateSizeVisibility();
  }

  const sizeRow = document.createElement('div');
  sizeRow.className = 'toolbar-size';
  const sizeLabel = document.createElement('label');
  sizeLabel.textContent = 'Brush size';
  const sizeInput = document.createElement('input');
  sizeInput.type = 'range';
  sizeInput.step = '1';
  const sizeValue = document.createElement('span');
  sizeValue.className = 'toolbar-size-value';

  /** Current brush size in mm (for cross-unit conversion when switching tools/units). */
  function getBrushSizeMmFromState(): number {
    if (state.brushUnit === 'inches') return state.brushSizeValue * INCH_TO_MM;
    if (state.brushUnit === 'feet') return state.brushSizeValue * FEET_TO_MM;
    return state.brushSizeValue * YARD_TO_MM;
  }

  const isGreen = () => state.activeTool === 'green';
  const isYardsOnlyTool = () =>
    ['fairway', 'firstCut', 'secondCut', 'green', 'bunker', 'waste', 'water', 'cartPath', 'outOfBounds', 'hazard', 'tee'].includes(
      state.activeTool
    );

  function updateBrushRangeAndValue() {
    if (isYardsOnlyTool()) {
      const mm = getBrushSizeMmFromState();
      state.brushUnit = 'yards';
      state.brushSizeValue = Math.max(MIN_YARDS, Math.min(MAX_YARDS, Math.round((mm / YARD_TO_MM) * 10) / 10));
      sizeInput.min = String(MIN_YARDS);
      sizeInput.max = String(MAX_YARDS);
      sizeInput.step = '0.1';
      sizeInput.value = String(state.brushSizeValue);
      sizeValue.textContent = `${state.brushSizeValue} yd`;
      return;
    }
    if (state.brushUnit === 'inches') {
      sizeInput.min = String(MIN_INCHES);
      sizeInput.max = String(MAX_INCHES);
      sizeInput.step = '1';
      state.brushSizeValue = Math.max(MIN_INCHES, Math.min(MAX_INCHES, Math.round(state.brushSizeValue)));
    } else if (state.brushUnit === 'feet') {
      sizeInput.min = String(MIN_FEET);
      sizeInput.max = String(MAX_FEET);
      sizeInput.step = '0.5';
      state.brushSizeValue = Math.max(MIN_FEET, Math.min(MAX_FEET, Math.round(state.brushSizeValue * 2) / 2));
    } else {
      sizeInput.min = String(MIN_YARDS);
      sizeInput.max = String(MAX_YARDS);
      sizeInput.step = '0.1';
      state.brushSizeValue = Math.max(MIN_YARDS, Math.min(MAX_YARDS, Math.round(state.brushSizeValue * 10) / 10));
    }
    sizeInput.value = String(state.brushSizeValue);
    const unitSuffix = state.brushUnit === 'inches' ? ' in' : state.brushUnit === 'feet' ? ' ft' : ' yd';
    sizeValue.textContent = `${state.brushSizeValue}${unitSuffix}`;
  }

  sizeInput.addEventListener('input', () => {
    if (isYardsOnlyTool()) {
      state.brushUnit = 'yards';
      state.brushSizeValue = Math.round(Number(sizeInput.value) * 10) / 10;
      sizeValue.textContent = `${state.brushSizeValue} yd`;
      return;
    }
    state.brushSizeValue =
      state.brushUnit === 'inches'
        ? Number(sizeInput.value)
        : state.brushUnit === 'feet'
          ? Math.round(Number(sizeInput.value) * 2) / 2
          : Math.round(Number(sizeInput.value) * 10) / 10;
    const unitSuffix = state.brushUnit === 'inches' ? ' in' : state.brushUnit === 'feet' ? ' ft' : ' yd';
    sizeValue.textContent = `${state.brushSizeValue}${unitSuffix}`;
  });

  const unitWrap = document.createElement('div');
  unitWrap.className = 'toolbar-brush-units';
  const inBtn = document.createElement('button');
  inBtn.type = 'button';
  inBtn.className = 'unit-btn';
  inBtn.textContent = 'in';
  inBtn.title = 'Inches (1–36)';
  const ftBtn = document.createElement('button');
  ftBtn.type = 'button';
  ftBtn.className = 'unit-btn';
  ftBtn.textContent = 'ft';
  ftBtn.title = 'Feet (0.5–3)';
  const ydBtn = document.createElement('button');
  ydBtn.type = 'button';
  ydBtn.className = 'unit-btn';
  ydBtn.textContent = 'yd';
  ydBtn.title = 'Yards (1–10)';

  function setBrushUnit(unit: BrushUnit) {
    if (unit === state.brushUnit || !isGreen()) return;
    const mm = getBrushSizeMmFromState();
    state.brushUnit = unit;
    if (unit === 'inches') {
      state.brushSizeValue = Math.max(MIN_INCHES, Math.min(MAX_INCHES, Math.round(mm / INCH_TO_MM)));
    } else if (unit === 'feet') {
      state.brushSizeValue = Math.max(MIN_FEET, Math.min(MAX_FEET, Math.round((mm / FEET_TO_MM) * 2) / 2));
    } else {
      state.brushSizeValue = Math.max(MIN_YARDS, Math.min(MAX_YARDS, Math.round((mm / YARD_TO_MM) * 10) / 10));
    }
    inBtn.classList.toggle('active', state.brushUnit === 'inches');
    ftBtn.classList.toggle('active', state.brushUnit === 'feet');
    ydBtn.classList.toggle('active', state.brushUnit === 'yards');
    updateBrushRangeAndValue();
  }

  inBtn.addEventListener('click', () => setBrushUnit('inches'));
  ftBtn.addEventListener('click', () => setBrushUnit('feet'));
  ydBtn.addEventListener('click', () => setBrushUnit('yards'));

  unitWrap.append(inBtn, ftBtn, ydBtn);
  updateBrushRangeAndValue();

  sizeRow.append(sizeLabel, sizeInput, sizeValue, unitWrap);

  const elevationRow = document.createElement('div');
  elevationRow.className = 'toolbar-elevation';
  const elevAmountLabel = document.createElement('label');
  elevAmountLabel.textContent = 'Amount (per click)';
  const elevAmountWrap = document.createElement('div');
  elevAmountWrap.className = 'toolbar-elevation-amount-btns';
  const elevAmountBtns: HTMLButtonElement[] = [];
  for (const inches of [3, 6, 12, 24] as const) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tool-btn elevation-amount-btn';
    btn.textContent = `${inches}"`;
    btn.setAttribute('data-elev-amount', String(inches));
    btn.setAttribute('title', `${inches} inches per click`);
    btn.addEventListener('click', () => {
      state.elevationAmountInches = inches;
      elevAmountBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-elev-amount') === String(inches)));
    });
    elevAmountWrap.appendChild(btn);
    elevAmountBtns.push(btn);
  }
  elevationRow.append(elevAmountLabel, elevAmountWrap);

  const elevSizeLabel = document.createElement('label');
  elevSizeLabel.textContent = 'Area size';
  const elevSizeWrap = document.createElement('div');
  elevSizeWrap.className = 'toolbar-elevation-size-btns';
  const elevSizeBtns: HTMLButtonElement[] = [];
  const elevSizeOpts: { size: ElevationSize; label: string; yards: number }[] = [
    { size: 'small', label: 'S', yards: 2 },
    { size: 'medium', label: 'M', yards: 5 },
    { size: 'large', label: 'L', yards: 10 },
  ];
  for (const opt of elevSizeOpts) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tool-btn elevation-size-btn';
    btn.textContent = opt.label;
    btn.setAttribute('data-elev-size', opt.size);
    btn.setAttribute('title', `${opt.yards} yd radius`);
    btn.addEventListener('click', () => {
      state.elevationSize = opt.size;
      elevSizeBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-elev-size') === opt.size));
    });
    elevSizeWrap.appendChild(btn);
    elevSizeBtns.push(btn);
  }
  elevationRow.append(elevSizeLabel, elevSizeWrap);

  const elevEdgeLabel = document.createElement('label');
  elevEdgeLabel.textContent = 'Edge';
  const elevEdgeWrap = document.createElement('div');
  elevEdgeWrap.className = 'toolbar-elevation-edge-btns';
  const elevEdgeBtns: HTMLButtonElement[] = [];
  for (const mode of ['smooth', 'steep'] as const) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tool-btn elevation-edge-btn';
    btn.textContent = mode === 'smooth' ? 'Smooth' : 'Steep';
    btn.setAttribute('data-elev-edge', mode);
    btn.setAttribute('title', mode === 'smooth' ? 'Gradual blend at edges' : 'Sharp drop-off at edges');
    btn.addEventListener('click', () => {
      state.elevationEdgeMode = mode;
      elevEdgeBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-elev-edge') === mode));
    });
    elevEdgeWrap.appendChild(btn);
    elevEdgeBtns.push(btn);
  }
  elevationRow.append(elevEdgeLabel, elevEdgeWrap);
  elevEdgeBtns[0]?.classList.add('active');

  const elevDirRow = document.createElement('div');
  elevDirRow.className = 'toolbar-elevation-direction';
  const elevDirLabel = document.createElement('label');
  elevDirLabel.textContent = 'Direction';
  const elevDirWrap = document.createElement('div');
  elevDirWrap.className = 'toolbar-elevation-direction-btns';
  const elevDirBtns: HTMLButtonElement[] = [];
  for (const dir of ['up', 'down'] as const) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tool-btn elevation-direction-btn';
    btn.setAttribute('data-elev-direction', dir);
    btn.setAttribute('title', dir === 'up' ? 'Raise elevation' : 'Lower elevation');
    const i = document.createElement('i');
    i.className = dir === 'up' ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down';
    i.setAttribute('aria-hidden', 'true');
    btn.appendChild(i);
    btn.addEventListener('click', () => {
      state.elevationDirection = dir;
      elevDirBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-elev-direction') === dir));
    });
    elevDirWrap.appendChild(btn);
    elevDirBtns.push(btn);
  }
  elevDirRow.append(elevDirLabel, elevDirWrap);
  elevationRow.appendChild(elevDirRow);
  elevDirBtns[0]?.classList.add('active');
  elevAmountBtns[1]?.classList.add('active'); // 6" default
  elevSizeBtns[1]?.classList.add('active'); // M default

  const treeSizeRow = document.createElement('div');
  treeSizeRow.className = 'toolbar-tree-size';
  const treeSizeLabel = document.createElement('label');
  treeSizeLabel.textContent = 'Tree size';
  const treeSizeWrap = document.createElement('div');
  treeSizeWrap.className = 'toolbar-tree-size-btns';
  const treeSizeOpts: { size: TreeSize; label: string; diameterYd: number }[] = [
    { size: 'S', label: 'S', diameterYd: 5 },
    { size: 'M', label: 'M', diameterYd: 10 },
    { size: 'L', label: 'L', diameterYd: 20 },
  ];
  const treeSizeBtns: HTMLButtonElement[] = [];
  for (const opt of treeSizeOpts) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `tool-btn tree-size-btn tree-size-btn-${opt.size}`;
    btn.textContent = opt.label;
    btn.setAttribute('data-tree-size', opt.size);
    btn.title = `${opt.diameterYd} yd diameter`;
    btn.addEventListener('click', () => {
      state.treeSize = opt.size;
      treeSizeBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
    });
    treeSizeWrap.appendChild(btn);
    treeSizeBtns.push(btn);
  }
  treeSizeRow.append(treeSizeLabel, treeSizeWrap);

  const houseSizeRow = document.createElement('div');
  houseSizeRow.className = 'toolbar-house-size';
  const houseSizeLabel = document.createElement('label');
  houseSizeLabel.textContent = 'House size';
  const houseSizeWrap = document.createElement('div');
  houseSizeWrap.className = 'toolbar-house-size-btns';
  const houseSizeOpts: { size: HouseSize; label: string; diameterYd: number }[] = [
    { size: 'S', label: 'S', diameterYd: 8 },
    { size: 'M', label: 'M', diameterYd: 15 },
    { size: 'L', label: 'L', diameterYd: 25 },
  ];
  const houseSizeBtns: HTMLButtonElement[] = [];
  for (const opt of houseSizeOpts) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `tool-btn house-size-btn house-size-btn-${opt.size}`;
    btn.textContent = opt.label;
    btn.setAttribute('data-house-size', opt.size);
    btn.title = `${opt.diameterYd} yd diameter`;
    btn.addEventListener('click', () => {
      state.houseSize = opt.size;
      houseSizeBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
    });
    houseSizeWrap.appendChild(btn);
    houseSizeBtns.push(btn);
  }
  houseSizeRow.append(houseSizeLabel, houseSizeWrap);

  const teePositionColorRow = document.createElement('div');
  teePositionColorRow.className = 'toolbar-tee-position-color';
  const teePositionColorLabel = document.createElement('label');
  teePositionColorLabel.textContent = 'Tee color';
  const teePositionColorWrap = document.createElement('div');
  teePositionColorWrap.className = 'toolbar-tee-position-color-btns';
  const TEE_COLOR_OPTS: TeePositionColorKey[] = ['red', 'gold', 'white', 'gray', 'blue', 'black'];
  const teePositionColorBtns: HTMLButtonElement[] = [];
  for (const key of TEE_COLOR_OPTS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tool-btn tee-position-color-btn';
    btn.title = key;
    btn.style.backgroundColor = TEE_POSITION_COLORS[key];
    btn.setAttribute('data-tee-color', key);
    btn.addEventListener('click', () => {
      state.teePositionColor = key;
      teePositionColorBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-tee-color') === key));
    });
    teePositionColorWrap.appendChild(btn);
    teePositionColorBtns.push(btn);
  }
  teePositionColorRow.append(teePositionColorLabel, teePositionColorWrap);

  const pinDayRow = document.createElement('div');
  pinDayRow.className = 'toolbar-pin-day';
  const pinDayLabel = document.createElement('label');
  pinDayLabel.textContent = 'Flag day';
  const pinDayWrap = document.createElement('div');
  pinDayWrap.className = 'toolbar-pin-day-btns';
  const PIN_DAY_OPTS: PinDay[] = ['thu', 'fri', 'sat', 'sun'];
  const pinDayBtns: HTMLButtonElement[] = [];
  for (const day of PIN_DAY_OPTS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tool-btn pin-day-btn';
    btn.textContent = day.charAt(0).toUpperCase() + day.slice(1);
    btn.style.backgroundColor = PIN_DAY_COLORS[day];
    btn.style.color = day === 'fri' || day === 'sun' ? '#333' : day === 'thu' || day === 'sat' ? '#fff' : '#ccc';
    btn.setAttribute('data-pin-day', day);
    btn.setAttribute('title', day === 'thu' ? 'Thursday' : day === 'fri' ? 'Friday' : day === 'sat' ? 'Saturday' : 'Sunday');
    btn.addEventListener('click', () => {
      state.pinDay = day;
      pinDayBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-pin-day') === day));
    });
    pinDayWrap.appendChild(btn);
    pinDayBtns.push(btn);
  }
  pinDayRow.append(pinDayLabel, pinDayWrap);
  pinDayBtns[0]?.classList.add('active');

  function updateSizeVisibility() {
    const isBrushOrPen =
      ['fairway', 'firstCut', 'secondCut', 'green', 'bunker', 'waste', 'water', 'cartPath', 'outOfBounds', 'hazard', 'tee'].includes(
        state.activeTool
      ) && state.activeTool !== 'eraser';
    sizeRow.style.display = isBrushOrPen ? 'flex' : 'none';
    unitWrap.style.display = isGreen() ? 'flex' : 'none';
    if (isBrushOrPen) updateBrushRangeAndValue();
    if (isGreen()) {
      inBtn.classList.toggle('active', state.brushUnit === 'inches');
      ftBtn.classList.toggle('active', state.brushUnit === 'feet');
      ydBtn.classList.toggle('active', state.brushUnit === 'yards');
    }
    elevationRow.style.display = state.activeTool === 'elevation' ? 'flex' : 'none';
    treeSizeRow.style.display = state.activeTool === 'tree' ? 'flex' : 'none';
    houseSizeRow.style.display = state.activeTool === 'house' ? 'flex' : 'none';
    teePositionColorRow.style.display = state.activeTool === 'teePosition' ? 'flex' : 'none';
    pinDayRow.style.display = state.activeTool === 'pin' ? 'flex' : 'none';
    if (state.activeTool === 'elevation') {
      elevDirBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-elev-direction') === state.elevationDirection));
      elevAmountBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-elev-amount') === String(state.elevationAmountInches)));
      elevSizeBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-elev-size') === state.elevationSize));
      elevEdgeBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-elev-edge') === state.elevationEdgeMode));
    }
    if (state.activeTool === 'tree') {
      treeSizeBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-tree-size') === state.treeSize));
    }
    if (state.activeTool === 'house') {
      houseSizeBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-house-size') === state.houseSize));
    }
    if (state.activeTool === 'teePosition') {
      teePositionColorBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-tee-color') === state.teePositionColor));
    }
    if (state.activeTool === 'pin') {
      pinDayBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-pin-day') === state.pinDay));
    }
  }

  updateSizeVisibility();

  root.appendChild(toolList);
  const modifierTarget = modifiersContainer ?? root;
  modifierTarget.appendChild(sizeRow);
  modifierTarget.appendChild(treeSizeRow);
  modifierTarget.appendChild(houseSizeRow);
  modifierTarget.appendChild(teePositionColorRow);
  modifierTarget.appendChild(pinDayRow);
  modifierTarget.appendChild(elevationRow);
  container.appendChild(root);

  showLayer(initialLayer);
  (toolList.querySelector(`[data-tool="${state.activeTool}"]`) as HTMLButtonElement)?.classList.add('active');
  updateSizeVisibility();

  const TREE_DIAMETER_YD: Record<TreeSize, number> = { S: 5, M: 10, L: 20 };
  const HOUSE_DIAMETER_YD: Record<HouseSize, number> = { S: 8, M: 15, L: 25 };

  function doSetActiveTool(id: ToolId) {
    state.activeTool = id;
    const inToolList = toolList.querySelector(`[data-tool="${id}"]`);
    if (inToolList) {
      toolList.querySelectorAll('.tool-btn').forEach((b) => b.classList.remove('active'));
      (inToolList as HTMLButtonElement).classList.add('active');
    }
    updateSizeVisibility();
    onActiveToolChange?.();
  }

  return {
    showLayer(layerId: LayerId) {
      showLayer(layerId);
      (toolList.querySelector(`[data-tool="${state.activeTool}"]`) as HTMLButtonElement)?.classList.add('active');
      onActiveToolChange?.();
    },
    refreshForHoleChange() {
      updatePar3DisabledState();
      updateGreenDisabledState();
      const curLayer = currentLayerId;
      const curTool = state.activeTool;
      const fallback = getToolsForLayer(curLayer).find(
        (t) =>
          !(PAR3_DISABLED_TOOLS.includes(t) && isPar3Hole()) &&
          !(t === GREEN_DISABLED_WHEN_EXISTS && hasGreenForCurrentHole())
      );
      if (
        fallback &&
        ((PAR3_DISABLED_TOOLS.includes(curTool) && isPar3Hole()) ||
          (curTool === GREEN_DISABLED_WHEN_EXISTS && hasGreenForCurrentHole()))
      ) {
        state.activeTool = fallback;
        toolList.querySelectorAll('.tool-btn').forEach((b) => b.classList.remove('active'));
        const activeBtn = toolList.querySelector<HTMLButtonElement>(`[data-tool="${fallback}"]`);
        if (activeBtn) activeBtn.classList.add('active');
        updateSizeVisibility();
        onActiveToolChange?.();
      }
    },
    setActiveTool(id: ToolId) {
      doSetActiveTool(id);
    },
    getState(): Readonly<ToolbarState> {
      return state;
    },
    getActiveTool(): ToolId {
      return state.activeTool;
    },
    getBrushSizeMm(): number {
      if (state.brushUnit === 'inches') return state.brushSizeValue * INCH_TO_MM;
      if (state.brushUnit === 'feet') return state.brushSizeValue * FEET_TO_MM;
      return state.brushSizeValue * YARD_TO_MM;
    },
    getTreeSizeMm(): number {
      const diameterYd = TREE_DIAMETER_YD[state.treeSize];
      const radiusMm = (diameterYd / 2) * YARD_TO_MM;
      return radiusMm;
    },
    getHouseSizeMm(): number {
      const diameterYd = HOUSE_DIAMETER_YD[state.houseSize];
      const radiusMm = (diameterYd / 2) * YARD_TO_MM;
      return radiusMm;
    },
    getElevationAmountInches(): ElevationAmountInches {
      return state.elevationAmountInches;
    },
    getElevationSizeRadiusMm(): number {
      const yards = state.elevationSize === 'small' ? 2 : state.elevationSize === 'medium' ? 5 : 10;
      return yards * YARD_TO_MM;
    },
    getElevationEdgeMode(): ElevationEdgeMode {
      return state.elevationEdgeMode;
    },
    getElevationDirection(): ElevationDirection {
      return state.elevationDirection;
    },
    getTeePositionColor(): TeePositionColorKey {
      return state.teePositionColor;
    },
    getPinDay(): PinDay {
      return state.pinDay;
    },
  };
}

export type Toolbar = ReturnType<typeof createToolbar>;
